<script setup lang="ts">
import { jsonLdScript, organizationLd, websiteLd } from '~/utils/jsonLd'

const route = useRoute()

const origin = useRequestURL().origin

// Заголовок главной — коммерческий: по запросу «статуэтки из гипса» человек
// ищет товар, а не страницу «О компании»
const description =
  'Статуэтки, бюсты и сувениры из художественного гипса от производителя. Собственная мастерская в Санкт-Петербурге с 1989 года, опт и розница, доставка по России.'

useHead({
  title: 'Статуэтки и сувениры из гипса от производителя',
  meta: [
    { name: 'description', content: description },
    { property: 'og:title', content: 'Smail Dell — статуэтки и сувениры из гипса от производителя' },
    { property: 'og:description', content: description },
    { property: 'og:url', content: origin },
    { property: 'og:image', content: `${origin}/android-chrome-512x512.png` },
  ],
  link: [{ rel: 'canonical', href: `${origin}/` }],
  script: [jsonLdScript(organizationLd(origin)), jsonLdScript(websiteLd(origin))],
})

// Тот же список, что в подвале и в каталоге — запрос к базе один на страницу.
// Пустые разделы не показываем: ссылка вела бы на страницу без товаров
const { data: allCategories } = await useCategories()
const categories = computed(() =>
  (allCategories.value ?? []).filter((category: any) => Number(category.products_count) > 0)
)
</script>

<template>
  <div style="max-width: 1200px; margin: 0 auto;">
    <h1>О компании</h1>
    <p>
      Мастерская основана и работает с 1989 года, специализируясь на создании сувениров из высокопрочного 
      художественного гипса и соответствует стандартам качества индустрии сувенирных изделий. Доверие 
      наших клиентов обусловлено гарантиями точности выполнения сроков и неизменным качеством.
    </p>

    <p>
      Наработан многолетний опыт создания сувенирной продукции и разработаны оригинальные технологии литья 
      гипсовых изделий. Для нас важна ориентированность на индивидуальный подход к заказчикам.
    </p>

    <p>
      Доверие клиентов к нашей сувенирной продукции сформировано через неизменно высокое качество изделий и 
      соблюдение сроков исполнения заказов, так как применяется высокопрочный художественный гипс с минимальной 
      пористостью и идеальной белизной. Гипсовые изделия гарантировано отличаются высокой прочностью, точной 
      детализацией и устойчивостью к механическим воздействиям.
    </p>

    <p>
      Наша торговая марка производит целый ряд продукции из гипса: бюсты, тематические статуэтки, архитектурные 
      миниатюры, корпоративные подарки и эксклюзивные изделия.
    </p>

    <p>
      За многолетний срок существования на рынке сувенирной продукции, рост числа заказов и расширение ассортимента 
      с 1989 только набирал обороты. Постепенное увеличение с 50 изделий в месяц до 1000 изделий и расширение клиентской 
      базы от 2 до 70 постоянных, довольных покупателей.
    </p>

    <p>
      У каждой созданной модели есть свой производственный цикл: разработка 3D-модели, подготовка формы, заливка гипса, 
      сушка, ручная обработка, окраска и лакировка. Каждый этап производства контролируется производителем, снижая 
      количество брака практически до нуля.
    </p>

    <p>
      По многим показателям гипс лидирует по экологичности, детализации изделия, весу и долговечности при умеренной 
      стоимости продукции.
    </p>

    <p>
      Показателем нашей добросовестной, качественной и профессиональной работы, было изготовлено по индивидуальному 
      заказу 500 корпоративных статуэток в течение 15 дней к юбилею компании-заказчика и как результат — своевременная 
      поставка, благодарственные письма и долгосрочное сотрудничество.
    </p>

    <p>
      На протяжении 30 лет плодотворной работы — отсутствие нареканий и рекламаций со стороны покупателей и заказчиков.
    </p>

    <p>
      Мастерская демонстрирует устойчивое развитие, сочетание культурных традиций и инноваций, расширяет линейку изделий 
      и географию поставок. Набор новых технологий повышает эффективность и художественный уровень продукции, гарантируя 
      качество и доверие.
    </p>

    <p>
      Сувенирная продукция, производимая нашей торговой маркой – это результат творческого подхода, уникальных знаний и 
      профессиональная работа мастеров, защищенная авторскими правами.
    </p>

    <p>
      Наша торговая марка, единственная в своем роде, достойно занимающая свое место на Витрине брендов Санкт-Петербурга.
    </p>

    <p style="text-align: center;">
      <img src="/img/main-page/products_smiledell.png" width="100%" style="max-width: 795px;" />
    </p>

    <!-- Ссылки в разделы прямо с главной: она собирает почти все внешние
         ссылки на сайт, и отсюда вес доходит до разделов лучше, чем
         из сквозного подвала. Человеку, дочитавшему про мастерскую,
         это тоже короткая дорога в каталог -->
    <h2 class="home-sections__title">Разделы каталога</h2>
    <p class="home-sections">
      <NuxtLink
        v-for="category in categories"
        :key="category.id"
        :to="`/catalog/${category.alias}`"
        class="home-sections__link"
      >{{ category.name }}</NuxtLink>
      <NuxtLink to="/catalog/seasons" class="home-sections__link">Сезонные товары</NuxtLink>
    </p>
  </div>

</template>

<style>
p {
  text-align: justify;
}

.home-sections__title {
  margin-bottom: 6px;
  font-size: 20px;
}

/* Строкой, а не плитками: блок не должен перетягивать на себя внимание
   с рассказа о мастерской */
.home-sections {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  text-align: left;
}

.home-sections__link {
  color: var(--el-color-primary);
  text-decoration: none;
  white-space: nowrap;
}

.home-sections__link:hover {
  text-decoration: underline;
}
</style>
